import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import {
  isBusinessHoursAccessRouteAllowed,
  validateLoggedInRoutes,
} from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { installProductAnalytics } from '../helpers/productAnalytics';

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
const INBOX_SETUP_STEP = 'inbox_setup';
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === INBOX_SETUP_STEP ? 'onboarding/inbox-setup' : 'onboarding';

const onboardingSteps = () => {
  const { onboardingInboxSetupEnabled } =
    store.getters['globalConfig/get'] || {};

  return onboardingInboxSetupEnabled
    ? [...ONBOARDING_STEPS, INBOX_SETUP_STEP]
    : ONBOARDING_STEPS;
};

export const router = createRouter({ history: createWebHistory(), routes });

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  if (!userAccount) {
    const nextRoute = validateLoggedInRoutes(to, user);
    return next(frontendURL(nextRoute));
  }
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    onboardingSteps().includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  const currentAccountId = Number(store.getters.getCurrentAccountId);
  const accessAction =
    currentAccountId && currentAccountId !== routeAccountId
      ? 'fetch'
      : 'ensure';
  await store.dispatch(
    `agentBusinessHoursAccess/${accessAction}`,
    routeAccountId
  );
  const isBusinessHoursRestricted =
    store.getters['agentBusinessHoursAccess/isRestricted'](routeAccountId);

  if (isBusinessHoursRestricted && !isBusinessHoursAccessRouteAllowed(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/inbox-view`));
  }

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding
      ? onboardingPath(userAccount?.onboarding_step)
      : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  installProductAnalytics({ router, store });
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
